import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { me, setToken, getToken } from "@/lib/api";

const AuthCtx = createContext({
  user: null,
  loading: true,
  setUser: () => {},
  refresh: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const u = await me();
      setUser(u);
      return u;
    } catch {
      setToken("");
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're handling an OAuth callback (hash contains session_id), skip /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(() => {
    setToken("");
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render when this provider re-renders
  // for unrelated reasons.
  const value = useMemo(
    () => ({ user, loading, setUser, refresh, logout }),
    [user, loading, refresh, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
